import React, { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { devDashboard, deptNeon, deptColor, formatDurationShort } from '@battleops/shared';
import { useDeptGoals } from '@battleops/client/src/data/useDeptGoals';
import { useTaskModal } from '../../hooks';
import Kpi from '../Kpi';
import { StackedBars, Sparkline, SplitBar, Legend, OTHER } from '../charts';
import { distinctColors } from '../../lib/colors';
import { ScoreHero } from './ScoreCard';
import { PinnedProjects } from './PinnedProjects';
import { TONE } from './DeptParts';
import { weekOf } from '../DeskPanel';
import Icon from '../Icon';
import s from '../../pages/Home.module.css';
import d from './DevDashboard.module.css';
import BoardCard from './BoardCard';

// Dev's Home (Lj, Oct 11: "polished and premium — give us ALL the data for the Dev department, it's a dashboard"; the
// All Home is the bar): the score hero, then the All Home's own pieces — the Working-on hero with its live timer
// beside four KPI tiles, the 14-day stacked chart, segmented bars, the streak strip — with Dev's data in every one:
// hours (today / week / 14 days by app project, the running timer live), shipping, issues, the plan, the apps pinned
// to Home, needs you. Counts come from shared devDashboard / devEngine — the goals' own counts.

const H = 3600000;
const hrs = (ms) => (ms >= H ? `${(ms / H).toFixed(1)}h` : formatDurationShort(ms));
const shortDay = (date) => new Date(`${date}T12:00:00`).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
const weekLabel = (from) => `Week of ${new Date(`${from}T12:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;

/** The project colours for one chart: each keeps its own unless a busier one has a look-alike; Other is grey. */
function useSeriesColors(series) {
  return useMemo(() => {
    const m = distinctColors(series.filter((x) => x.id !== 'other').map((x) => x.id), (id) => series.find((x) => x.id === id)?.color);
    return (id) => (id === 'other' ? OTHER : m.get(id) || OTHER);
  }, [series]);
}

/** Rows of name · value with a full-width bar under each (the card is narrow — HBars' fixed columns don't fit). */
function BarList({ rows, color }) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <div className={d.barList}>
      {rows.map((r) => (
        <div key={r.id} className={d.barRow} title={`${r.label}: ${r.value}`}>
          <span className={d.barTop}><span className={d.rowText}>{r.label}</span><b>{r.value}</b></span>
          <span className={d.barTrack}><span style={{ width: `${(r.value / max) * 100}%`, background: r.color || color }} /></span>
        </div>
      ))}
    </div>
  );
}

/** Dev hours by app project, the last 14 days — the All Home's "Last 14 days" with Dev's entries. */
export function DevHoursCard({ x, goalHours }) {
  const colorOf = useSeriesColors(x.hours.series);
  const bars = x.hours.perDay.map((day) => ({
    key: day.date, label: String(new Date(`${day.date}T12:00:00`).getDate()), tip: shortDay(day.date),
    segments: day.parts.filter((p) => p.ms > 0).map((p) => ({ id: p.id, label: x.hours.series.find((t) => t.id === p.id)?.name || 'Other', value: p.ms / H, color: colorOf(p.id) })),
  }));
  const target = goalHours ? Math.round((goalHours / 5) * 10) / 10 : null;
  return (
    <section className={s.card}>
      <div className={s.cardHead}><h2>Last 14 days</h2><span className={s.cardSub}>Dev hours by app project{target ? ` · dashed line = ${target} h (the Dev hours goal ÷ 5)` : ''}{x.hours.liveMs ? ' · counting the running timer' : ''}</span></div>
      <StackedBars data={bars} height={230} target={target} format={(v) => (v >= 1 ? `${v.toFixed(v >= 10 ? 0 : 1)}h` : `${Math.round(v * 60)}m`)} />
      <Legend items={x.hours.series.map((t) => ({ id: t.id, label: `${t.name} · ${hrs(t.ms)}`, color: colorOf(t.id) }))} />
    </section>
  );
}

/** Issues (#bug): opened vs fixed in 14 days, open now each day, open by app, the oldest. */
export function DevIssuesCard({ x, onOpen }) {
  const open = x.engine.open;
  return (
    <section className={s.card}>
      <div className={s.cardHead}><h2>Issues</h2><span className={s.cardSub}>tasks tagged #bug · last 14 days</span></div>
      <div className={s.bigRow}>
        <div><div className={s.big}>{open.n}</div><div className={s.bigSub}>open now</div></div>
        <div><div className={s.big} style={{ color: x.issues.fixed14 ? TONE.met : undefined }}>{x.issues.fixed14}</div><div className={s.bigSub}>fixed</div></div>
        <div><div className={s.big}>{x.issues.opened14}</div><div className={s.bigSub}>opened</div></div>
      </div>
      <div className={d.spark}><Sparkline points={x.issues.openPerDay.map((p) => ({ key: p.date, label: shortDay(p.date), value: p.n }))} format={(v) => `${v} open`} color={TONE.close} height={44} /></div>
      {x.issues.byProject.length ? <div className={d.sub}>Open by app</div> : null}
      {x.issues.byProject.length ? <BarList rows={x.issues.byProject.slice(0, 5).map((r) => ({ id: r.project.id || 'none', label: r.project.name, value: r.n }))} color={TONE.close} /> : null}
      {open.rows.length ? <div className={d.sub}>Oldest</div> : null}
      {open.rows.slice(0, 3).map((r) => (
        <button key={r.task.id} type="button" className={d.row} onClick={() => onOpen(r.task.id)}>
          <span className={d.rowText}>{r.task.title}</span>
          <span className={d.age} style={{ color: r.ageDays > 14 ? TONE.over : r.ageDays > 7 ? TONE.close : undefined }}>{r.ageDays == null ? '—' : `${r.ageDays} d`}</span>
        </button>
      ))}
      {!open.n && !x.issues.fixed14 ? <div className={d.quiet}>No #bug tasks in 14 days.</div> : null}
    </section>
  );
}

export default function DevDashboard({ dept, upNext }) {
  const navigate = useNavigate();
  const details = useTaskModal();
  const { view, data, week, signals } = useDeptGoals(dept);
  const x = useMemo(() => devDashboard(dept, data), [dept, data]);
  const neon = deptNeon(dept);
  const colorOf = useSeriesColors(x.hours.series);
  if (!view) return null;
  const goalHours = view.goals.find((g) => g.key === 'hoursWorked' && g.period === 'week')?.target || null;
  const e = x.engine;
  const vs = (a, b) => (b ? (a >= b ? 'up' : 'down') : null);
  const split = x.hours.todayByProject.map((p) => ({ id: p.id, label: p.name, value: p.ms, color: colorOf(p.id) }));
  const shipBars = x.shipped.weeks.map((w) => ({ key: w.from, label: new Date(`${w.from}T12:00:00`).getDate().toString(), tip: weekLabel(w.from), segments: [{ id: 'shipped', label: 'Shipped', value: w.n, color: neon[1] }] }));
  return (
    <div style={{ '--a': deptColor(dept) }}>
      <ScoreHero dept={dept} view={view} week={week} data={data} subtitle={weekOf()} onOpen={(goalId) => navigate(`/department/${dept.id}${goalId ? `?goal=${goalId}` : view.goals.length ? '' : '?add=1'}`)} />

      {/* Working on (the live timer) beside the KPI tiles — the All Home's top row */}
      <div className={s.topGrid}>
        {upNext}
        <div className={s.kpis}>
          <Kpi label="Clocked today · Dev" value={hrs(x.hours.today)} tone={vs(x.hours.today, x.hours.avg)}
            sub={x.hours.avg ? `${x.hours.today >= x.hours.avg ? '▲' : '▼'} vs ${hrs(x.hours.avg)} avg${x.hours.liveMs ? ' · live' : ''}` : 'the 14-day average builds up'}
            points={x.hours.perDay.map((p) => ({ key: p.date, label: shortDay(p.date), value: p.ms / H }))} format={(v) => `${v.toFixed(1)}h`} color={neon[1]} to="/time" />
          <Kpi label="Shipped this week" value={String(e.shipped.n)} tone={e.shipped.n === x.shipped.avg4 ? null : vs(e.shipped.n, x.shipped.avg4)}
            sub={!e.shipped.n && !x.shipped.avg4 ? 'none in the last 4 weeks either' : e.shipped.n === x.shipped.avg4 ? `level with the last 4 weeks (${Math.round(x.shipped.avg4 * 10) / 10} a week)` : `${e.shipped.n > x.shipped.avg4 ? '▲' : '▼'} vs ${Math.round(x.shipped.avg4 * 10) / 10} a week (last 4)`}
            points={x.shipped.weeks.map((w) => ({ key: w.from, label: weekLabel(w.from), value: w.n }))} format={(v) => `${v} shipped`} color="var(--c-success)" onClick={() => document.getElementById('dev-shipping')?.scrollIntoView({ behavior: 'smooth' })} />
          <Kpi label="Issues fixed this week" value={String(e.fixed.n)} tone={e.open.n ? 'down' : 'up'}
            sub={`${e.open.n} open${e.open.oldest?.ageDays != null ? ` · oldest ${e.open.oldest.ageDays} d` : ''}`}
            points={x.issues.openPerDay.map((p) => ({ key: p.date, label: shortDay(p.date), value: p.n }))} format={(v) => `${v} open`} color={TONE.close} onClick={() => document.getElementById('dev-issues')?.scrollIntoView({ behavior: 'smooth' })} />
          <Kpi label="This week · Dev" value={hrs(x.hours.week)} tone={vs(x.hours.week, x.hours.lastWeek)}
            sub={x.hours.lastWeek ? `last week ${hrs(x.hours.lastWeek)}` : 'first week tracked here'}
            points={x.hours.weeks.map((w) => ({ key: w.from, label: weekLabel(w.from), value: w.ms / H }))} format={(v) => `${v.toFixed(1)}h`} color="var(--c-gold)" to="/time" />
        </div>
      </div>

      {/* the 14 days, today, the plan, issues */}
      <div className={`${s.chartGrid} ${d.even}`}>
        <DevHoursCard x={x} goalHours={goalHours} />
        <div className={s.stack}>
          <section className={s.card}>
            <div className={s.cardHead}><h2>Today on Dev</h2><span className={s.cardSub}>{x.hours.today ? `${hrs(x.hours.today)} on Dev tasks, by app${x.hours.liveMs ? ' · the running timer counted live' : ''}` : 'Nothing clocked on Dev tasks yet today'}</span></div>
            {split.length ? <SplitBar parts={split} format={formatDurationShort} /> : <div className={d.ghost} />}
          </section>
          <section className={s.card}>
            <div className={s.cardHead}><h2>Plan hit rate · Dev</h2><span className={s.cardSub}>Dev tasks dated a day and finished that day · 14 days</span></div>
            <div className={s.bigRow}>
              <div><div className={s.big}>{x.plan.avg == null ? '—' : `${x.plan.avg}%`}</div><div className={s.bigSub}>{x.plan.avg == null ? 'no Dev task dated yet' : 'average'}</div></div>
              <div><div className={s.big}>{x.plan.streak}<span className={s.bigUnit}> day{x.plan.streak === 1 ? '' : 's'}</span></div><div className={s.bigSub}>streak with a Dev task done</div></div>
            </div>
            <div className={s.dots} aria-label="Daily hit rate, Dev">
              {x.plan.days.map((p) => <span key={p.date} title={`${shortDay(p.date)} · ${p.rate == null ? 'nothing dated' : `${p.done}/${p.planned} done`}`} data-level={p.rate == null ? 'none' : p.rate >= 80 ? 'high' : p.rate >= 50 ? 'mid' : 'low'} />)}
            </div>
          </section>
        </div>
        <div id="dev-issues"><DevIssuesCard x={x} onOpen={(id) => details.open(id)} /></div>
      </div>

      {/* the Dev board as mini bars (B7) — Backlog · Next · Doing · Review · Done, WIP over in red */}
      <BoardCard dept={dept} data={data} />

      {/* the apps pinned to this Home — where Next moves sat */}
      <PinnedProjects dept={dept} />

      {/* shipping · open work · needs you */}
      <div className={`${s.chartGrid} ${d.even}`}>
        <section className={s.card} id="dev-shipping">
          <div className={s.cardHead}><h2>Shipping</h2><span className={s.cardSub}>#shipped per week · 8 weeks · built, not live (#deploy) with its age</span></div>
          <div className={s.bigRow}>
            <div><div className={s.big}>{e.shipped.n}</div><div className={s.bigSub}>shipped this week</div></div>
            <div><div className={s.big}>{e.improvements.n}</div><div className={s.bigSub}>improvements</div></div>
            <div><div className={s.big} style={{ color: e.waiting.n ? TONE.behind : undefined }}>{e.waiting.n}</div><div className={s.bigSub}>not live</div></div>
          </div>
          <div style={{ marginTop: 12 }}><StackedBars data={shipBars} height={150} format={(v) => String(Math.round(v))} /></div>
          {e.waiting.rows.slice(0, 4).map((r) => (
            <button key={r.task.id} type="button" className={d.row} onClick={() => details.open(r.task.id)}>
              <Icon name="upload-cloud" size={14} style={{ color: TONE.behind, flex: 'none' }} /><span className={d.rowText}>{r.task.title}</span>
              <span className={d.age} style={{ color: TONE.behind }}>waiting {r.ageDays ?? '?'} d</span>
            </button>
          ))}
        </section>
        <section className={s.card}>
          <div className={s.cardHead}><h2>Open work</h2><span className={s.cardSub}>open Dev tasks by app · Dev tasks done each day</span></div>
          <div className={s.bigRow}>
            <div><div className={s.big}>{x.openByProject.reduce((a, r) => a + r.n, 0)}</div><div className={s.bigSub}>open</div></div>
            <div><div className={s.big}>{x.done.today}</div><div className={s.bigSub}>done today · avg {Math.round(x.done.avg * 10) / 10}</div></div>
          </div>
          <div className={d.spark}><Sparkline points={x.done.perDay.map((p) => ({ key: p.date, label: shortDay(p.date), value: p.n }))} format={(v) => `${v} done`} color="#3F7FE6" height={44} /></div>
          <BarList rows={x.openByProject.slice(0, 6).map((r) => ({ id: r.project.id || 'none', label: r.project.name, value: r.n, color: r.project.id ? colorOf(r.project.id) : OTHER }))} />
        </section>
        <section className={s.card}>
          <div className={s.cardHead}><h2>Needs you</h2><span className="faint num">{signals.length}</span><span className={s.cardSub}>overdue, over budget, waiting on you — Dev</span></div>
          {signals.slice(0, 7).map((n, i) => (
            <button key={`${n.kind}${i}`} type="button" className={d.row} onClick={() => (n.taskId ? details.open(n.taskId) : n.projectId ? navigate(`/projects/${n.projectId}`) : null)}>
              <Icon name={n.kind === 'overdue' ? 'clock' : n.kind === 'over' ? 'alert-triangle' : n.kind === 'receipt' ? 'file-text' : 'briefcase'} size={14} style={{ color: n.kind === 'overdue' ? TONE.over : 'var(--c-text-tertiary)', flex: 'none' }} />
              <span className={d.rowText}>{n.title}</span>{n.detail ? <span className={d.age}>{n.detail}</span> : null}
            </button>
          ))}
          {!signals.length ? <div className={d.quiet}>Nothing waiting on you in Dev.</div> : null}
        </section>
      </div>
    </div>
  );
}
